'use client';
import { useState, useRef, useEffect } from 'react';
import Section from '@repo/ui/src/components/Section';
import LabeledValues, { LabeledValue } from './_LabeledValues';
import TextIconBoxes, { TextIconSingleBoxes, TextIconComboBox } from './_TextIconBoxes';
import { HeaderVariationProps } from '@repo/ui/src/types/headerVariation';
import ImageAndText, { ImageText } from './_ImageAndText';
import IconDescription, { IconAndDescriptionProps } from './_IconDescription';
import { ImageProps } from '@repo/ui/src/types/image';
import { classNames } from '@repo/ui/src/utils/classNames';

enum BackgroundStyle {
  Black = 'Black',
  TextureDark = 'TextureDark',
  White = 'White',
  Warm = 'Warm',
}

interface ProductTabs {
  name: string;
  labeledValues?: Array<LabeledValue>;
  leftProductImage: ImageProps;
  rightProductImage: ImageProps;
  textIconComboBox?: TextIconComboBox;
  textIconSingleBoxes?: Array<TextIconSingleBoxes>;
  rightProductImageText: ImageText;
  leftProductImageText: ImageText;
  iconAndDescription?: IconAndDescriptionProps;
}
interface Props {
  heading?: string;
  headerVariation: HeaderVariationProps;
  background: BackgroundStyle;
  tabAriaLabel: string;
  products?: Array<ProductTabs>;
  _testID?: string;
  griddle?: boolean;
  tabs?: Array<ProductTabs>;
}

const GrillCapacityAndSpecs = ({
  heading,
  background = BackgroundStyle.Black,
  products,
  _testID,
  tabAriaLabel = 'Select a Product',
  tabs,
}: Props) => {
  const [currentTab, setCurrentTab] = useState(0);
  const productTabs = products || tabs;

  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const [indicatorStyle, setIndicatorStyle] = useState({ left: '0px', width: '0px' });

  useEffect(() => {
    const tab = tabRefs.current[currentTab];
    if (tab) {
      setIndicatorStyle({
        left: `${tab.offsetLeft}px`,
        width: `${tab.offsetWidth}px`,
      });
    }
  }, [currentTab]);

  if (!productTabs?.length) return null;

  const selectedProduct = productTabs[currentTab];
  const darkMode =
    background === BackgroundStyle.Black || background === BackgroundStyle.TextureDark;

  const tabsVisible = productTabs.length > 1;

  return (
    <Section
      black={background === BackgroundStyle.Black}
      white={background === BackgroundStyle.White}
      dark={background === BackgroundStyle.TextureDark}
      texture={background === BackgroundStyle.TextureDark}
      largePadding
      gutterX
      gutterY
      monetateTestId={`grill_capacity_and_specs_${_testID}`}
      className="tw-flex tw-flex-col"
    >
      {heading && (
        <h2
          className={classNames(
            'tra-text-web-heading-2 tw-self-center',
            darkMode ? 'tw-text-neutral-100' : 'tw-text-neutral-500',
            !tabsVisible && 'tw-pb-20 tw-mb-0',
          )}
        >
          {heading}
        </h2>
      )}
      {tabsVisible && (
        <div className="tw-relative tw-flex tw-items-center tw-justify-center tw-pt-1 tw-pb-5 md:tw-pb-10">
          <ul className="tw-relative tw-flex tw-list-none tw-p-0 tw-m-0" role="tablist" aria-label={tabAriaLabel}>
            {productTabs.map(({ name }, index) => (
              <li key={name} className="tw-list-none">
                <button
                  id={`product-tab-${index}`}
                  ref={(el) => {
                    tabRefs.current[index] = el;
                  }}
                  onClick={() => setCurrentTab(index)}
                  className={classNames(
                    'tw-py-2 tw-mx-2 tw-transition-colors tra-text-web-sub-2 hover:tw-text-brand-300',
                    currentTab === index && 'tw-text-brand-300',
                    currentTab !== index && (darkMode ? '!tw-text-brand-50' : 'tw-text-neutral-600'),
                  )}
                  role="tab"
                  aria-controls={`product-panel-${index}`}
                  aria-selected={currentTab === index ? "true" : "false"}
                  aria-label={tabAriaLabel}
                >
                  {name}
                </button>
              </li>
            ))}
            <div
              className="tw-absolute tw-bottom-0 tw-h-1 tw-bg-brand-300 tw-transition-all tw-duration-300"
              style={indicatorStyle}
            />
          </ul>
        </div>
      )}
      <div className="tw-grid md:tw-max-w-[1200px] tw-m-auto">
        {productTabs.map(
          (
            { leftProductImage, rightProductImage, leftProductImageText, rightProductImageText },
            i,
          ) => (
            <div
              key={i}
              id={`product-panel-${i}`}
              role="tabpanel"
              aria-labelledby={`product-tab-${i}`}
              className={classNames(
                'tw-flex tw-grid-in-a tw-transition-opacity tw-duration-300 tw-justify-evenly tw-gap-5',
                i === currentTab ? 'tw-opacity-100' : 'tw-opacity-0',
              )}
            >
              <ImageAndText image={leftProductImage} text={leftProductImageText} />
              <ImageAndText image={rightProductImage} text={rightProductImageText} />
            </div>
          ),
        )}
      </div>
      <div className="tw-flex tw-flex-col tw-pt-20 tw-gap-10">
        {selectedProduct?.labeledValues && (
          <>
            <LabeledValues labeledValues={selectedProduct.labeledValues} darkMode={darkMode} />
            {selectedProduct.textIconComboBox && selectedProduct.textIconSingleBoxes && (
              <TextIconBoxes
                textIconSingleBoxes={selectedProduct.textIconSingleBoxes}
                textIconComboBox={selectedProduct.textIconComboBox}
                darkMode={darkMode}
              />
            )}
          </>
        )}
        {selectedProduct.iconAndDescription && (
          <IconDescription {...selectedProduct.iconAndDescription} />
        )}
      </div>
    </Section>
  );
};

export default GrillCapacityAndSpecs;
